import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  PaymentCardTeamMember,
  RateType
} from '../../hooks/usePaymentCards'
import { formatRateDisplay } from './PayRateCard'
import TeamMembersSection from './TeamMembersSection'

export type RateSummaryItem = {
  name: string
  rate: number | string
  rateType?: RateType
}

type PayRateCardSummaryProps = {
  effectiveFrom: string
  baseRate: { rate: number | string; rateType?: RateType } | null
  overrides: RateSummaryItem[]
  teamMembers?: PaymentCardTeamMember[]
  showTeamMembers?: boolean
  showDividerAfter?: boolean
}

const PayRateCardSummary = ({
  effectiveFrom,
  baseRate,
  overrides,
  teamMembers,
  showTeamMembers = true,
  showDividerAfter = false
}: PayRateCardSummaryProps) => {
  const { t } = useTranslation()

  return (
    <>
      <ViewSection>
        <ViewLabel level={2}>{t('EFFECTIVE_FROM')}</ViewLabel>
        <ViewValue>{effectiveFrom}</ViewValue>
      </ViewSection>

      <RatesRow>
        <ViewSection>
          <ViewLabel level={2}>{t('BASE_RATE')}</ViewLabel>
          <ViewValue>
            {baseRate
              ? formatRateDisplay(baseRate.rate, baseRate.rateType, t)
              : '-'}
          </ViewValue>
        </ViewSection>

        {overrides.map((override) => (
          <ViewSection key={override.name}>
            <ViewLabel level={2}>{override.name}</ViewLabel>
            <ViewValue>
              {formatRateDisplay(override.rate, override.rateType, t)}
            </ViewValue>
          </ViewSection>
        ))}
      </RatesRow>

      {showTeamMembers && (
        <TeamMembersSection
          label={t('CURRENTLY_ASSIGNED_TEAM_MEMBERS')}
          members={teamMembers}
          emptyText={t('NO_TEAM_MEMBERS_ASSIGNED')}
        />
      )}

      {showDividerAfter && <SummaryDivider />}
    </>
  )
}

const RatesRow = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
`

const ViewSection = styled.div``

const ViewLabel = styled(Text)`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const ViewValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const SummaryDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

export default PayRateCardSummary
